import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import {
  useCreateChatChannelMutation,
  useSendSMSMutation,
  useSMSUsersQuery,
} from "../../../api/network/chatApiServices";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { SMSUser } from "../../../api/types/Chat";
import svgIcons from "../../../assets/svg/svgIcons";
import AppSearchBox from "../../../components/searchBox";
import { APP_CONFIG } from "../../../constants/constants";
import { useChatContext } from "../contexts/chat.context";
import ChatListItem from "./chatListItem";
import MessageModeSelector from "./messageModeSelector";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useLoggedInUserData } from "../../../utils/user";

type TSMSAsideColumnProps = {
  wrapperClass?: string;
};

const SMSAsideColumn: FC<TSMSAsideColumnProps> = ({
  wrapperClass = "col-span-4 flex flex-col h-[calc(100vh-160px)]",
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "chatSection" });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const {
    selectedSMSUser,
    setSelectedSMSUser,
    dataSMSConversation,
    isFetchingSMSConversation,
  } = useChatContext();

  const [searchKeyword, setSearchKeyword] = useState<string>("");

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    setSearchKeyword(value);
  }, 500);

  const {
    data: dataSearchSMSUsers,
    isFetching: isFetchingSearchSMSUsers,
    refetch: refetchSearchSMSUsers,
  } = useSMSUsersQuery(
    { search: searchKeyword }
    // { skip: !searchKeyword }
  );
  // refetch SMS users on SMS conversation fetch, specifically to update unread count
  useEffect(() => {
    if (!!dataSMSConversation && !isFetchingSMSConversation) {
      refetchSearchSMSUsers();
    }
  }, [dataSMSConversation, isFetchingSMSConversation]);
  const { getUserACLAccessLevel } = useACL();
  const thisOrgDriverGroupAllowed = useLoggedInUserData("hasdrivers");
  const handleSMSUserClick = (user: SMSUser) => {
    if (user.driverid === selectedSMSUser) return;
    setSelectedSMSUser(user.driverid);

    return;
    // if a channel already exists, select it
    // const existingChannel
    //   = dataSMSChannels?.results
    //     ?.find(
    //       channel =>
    //         channel.members_info
    //           ?.find((member: any) => member.user_id === user.driverid) // TODO: needs fixing
    //     );
    // if (!!existingChannel) {
    //   debouncedSetSearchKeyword("");
    //   setSelectedSMSUser(existingChannel.channel_id);
    //   return;
    // }
    // // else create a new channel
    // createChatChannelAPITrigger(
    //   {
    //     channel_type: 1,
    //     members: [user.driverid],
    //   }
    // )
    //   .unwrap()
    //   .then((response) => {
    //     debouncedSetSearchKeyword("");
    //     setSelectedSMSUser(response?.channel_id);
    //   })
    //   .catch((error) => {
    //     console.error('error:', error);
    //   });
  };
  //const showCreateGroupSMSModal = modalsState.showCreateGroupSMSModal;
  // useEffect(() => {
  //   if (!showCreateGroupSMSModal) {
  //     setTimeout(() => {
  //       refetchSearchSMSUsers();
  //     }, 1000); 
  //   }
  // }, [showCreateGroupSMSModal, refetchSearchSMSUsers]);
  
  
  return (
    <div className={`relative ${wrapperClass}`}>
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="absolute flex flex-col text-[8px] pointer-events-none touch-none bottom-0 z-10">
          <span>
            selectedSMSUser: {JSON.stringify(selectedSMSUser, null, 2)}
          </span>
        </pre>
      )}

      {/* Messaging Mode Selector */}
      <MessageModeSelector />

      {/* Search Box */}
      <AppSearchBox
        initialValue={searchKeyword}
        placeholder={t("search_placeholder")}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedSetSearchKeyword(e.target.value)
        }
        onClear={() => {
          debouncedSetSearchKeyword("");
        }}
        wrapperClassName="w-full mb-4"
        inputClassName="w-full rounded-md border-gray-200 py-2.5 ps-10 shadow-sm sm:text-sm enabled:outline-accent-blue-dark"
      />

      {/* Items List */}
      {isFetchingSearchSMSUsers ? (
        <div className="flex-grow flex items-center justify-center">
          <img
            src={svgIcons.LoadingAnimation}
            alt="Loading animation"
            className="size-16"
          />
        </div>
      ) : (
        <ul
          className={`flex-grow flex flex-col overflow-y-auto${false ? " opacity-50 grayscale pointer-events-none touch-none" : ""
            }`}
        >
          {/* user search results */}
          {dataSearchSMSUsers?.results.length === 0 ? (
            <li>
              <p className="font-bold text-heading-gray888/70 py-4 text-center">
                {t("no_users_found")}
              </p>
            </li>
          ) : (
            <>
              {!!dataSearchSMSUsers?.results &&
                dataSearchSMSUsers?.results.length > 0 && (
                  <>
                    {[...dataSearchSMSUsers?.results] // clone
                      // sort by unread count and last message timestamp
                      ?.sort((a, b) => {
                        if (a?.unread_count === b?.unread_count) {
                          return b?.last_timestamp - a?.last_timestamp;
                        }
                        return b?.unread_count - a?.unread_count;
                      })
                      // render
                      ?.map((searchedUser, index) => (
                        <ChatListItem
                          key={index}
                          initials={
                            !!searchedUser?.description
                              ? searchedUser?.description
                                ?.split(" ")
                                .slice(0, 2)
                                .map((name: string) => name[0])
                                .join("")
                              : searchedUser?.driverid?.substring(0, 2)
                          }
                          isFirstItem={index === 0}
                          lastMessage={searchedUser?.last_message}
                          onClick={() => handleSMSUserClick(searchedUser)}
                          selected={selectedSMSUser === searchedUser?.driverid}
                          title={
                            searchedUser?.description ?? searchedUser?.driverid
                          }
                          smsTimestamp={searchedUser?.last_timestamp}
                          unreadCount={searchedUser?.unread_count}
                        />
                      ))}
                  </>
                )}
            </>
          )}
        </ul>
      )}

      {/* Send (Group) SMS Button */}
      <button
        type="button"
        className={
          "absolute bottom-4 right-4 rounded-lg border transition group p-2 cursor-pointer " +
          "border-accent-blue-pale bg-accent-blue-pale text-accent-blue-dark enabled:hover:bg-accent-blue-dark enabled:hover:text-white active:bg-accent-blue-dark enabled:focus:bg-accent-blue-dark enabled:focus:outline enabled:focus:-outline-offset-1 enabled:focus:outline-accent-blue-dark ring-accent-blue-pale"
        }
        onClick={() => {
          dispatch(
            setModalsData({ ...modalsState, showCreateGroupSMSModal: true })
          );
        }}
        disabled={thisOrgDriverGroupAllowed === "0" ||
          getUserACLAccessLevel(ACL_NAMES.ACL_MESSAGING_SEND_MSG) <=
          ROLE_ACCESS_LEVELS.READ
        }
      >
        <img
          className="relative translate-y-[2px] size-8 transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
          src={svgIcons.SMSIcon}
          alt="create group chat"
        />
      </button>
    </div>
  );
};
export default SMSAsideColumn;
